import { Spin } from 'antd'
import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Input } from 'sutra/components/Input/Input'
import { TextArea } from 'sutra/components/Input/TextArea'

import { useFeatureFlagGate } from 'features/project/feature-flags'
import { useTopBar } from 'features/project/project-layout'
import { useProjectStore } from 'features/project/store'
import {
  ContentWrapper,
  InsightDescription,
  InsightHeader,
  InsightTitle,
  PageContainer,
  PageShell,
  SectionCard,
} from 'features/trend-detection/components/insight-detail-styles'
import { InsightDetailSubHeader } from 'features/trend-detection/components/InsightDetailSubHeader'
import { useAgentConfig } from 'features/trend-detection/features/insight-agent/hooks/use-agent-config'
import { useAgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import { trendDetectionAgentsPath } from 'features/trend-detection/paths'

// Flag key is `general_agent`, not `insight_agent` — it predates the rename and
// carries live per-user overrides. Absent (not `false`) when unset, hence `!!`.
const INSIGHT_AGENT_FLAG = 'general_agent'

/**
 * Configuration page for one Reports Agent.
 *
 * Scope is deliberately narrow: see and edit what the agent IS — its name and
 * prompt. Run controls and produced insights are NOT here; output belongs in
 * the insights feed, so this page stays a config surface rather than a
 * half-dashboard.
 */
export const AgentDetailPage: React.FC = () => {
  const { agentUuid } = useParams<{ agentUuid: string }>()
  const projectId = useProjectStore(
    (s: { projectId: string | null }) => s.projectId,
  )
  const navigate = useNavigate()
  const service = useAgentInsightService()
  const { setBreadcrumbSubtitle } = useTopBar()

  const config = useAgentConfig(service, projectId, agentUuid)
  const agentsPath = projectId ? trendDetectionAgentsPath(projectId) : '/'

  // Redirects only once the flags request RESOLVES to "off"; a bare `!hasFlag`
  // effect fires while flags are still loading and bounces a user who does have
  // the flag off any hard refresh or deep link.
  useFeatureFlagGate(INSIGHT_AGENT_FLAG, agentsPath)

  useEffect(() => {
    setBreadcrumbSubtitle(config.agent?.name ?? null)
    return () => setBreadcrumbSubtitle(null)
  }, [config.agent?.name, setBreadcrumbSubtitle])

  // A non-editor reaches this page legitimately — the row menu offers "View"
  // for someone else's agent. What they must NOT get is an editable form: the
  // API 403s their save, and `useAgentConfig` maps every failure to "Please try
  // again", which asks them to retry something that can never succeed. Read-only
  // is the honest rendering of an agent you do not own.
  const isEditor = config.agent?.is_editor ?? false

  const canSave =
    isEditor &&
    config.isDirty &&
    config.name.trim().length > 0 &&
    config.prompt.trim().length > 0

  return (
    <PageShell>
      <InsightDetailSubHeader
        onBack={() => navigate(agentsPath)}
        backLabel="Back to agents"
        feedbackDisabled
      />
      <PageContainer>
        <ContentWrapper>
          {config.isLoading && <Spin />}
          {config.agent && (
            <>
              <InsightHeader>
                <InsightTitle>{config.agent.name}</InsightTitle>
                <InsightDescription>
                  {isEditor
                    ? 'Configuration for this Reports Agent.'
                    : 'Configuration for this Reports Agent. Only its creator or a project admin can change it.'}
                </InsightDescription>
              </InsightHeader>

              <SectionCard>
                <Field>
                  <FieldLabel htmlFor="agent-name">Agent name</FieldLabel>
                  <Input
                    id="agent-name"
                    value={config.name}
                    readOnly={!isEditor}
                    onChange={(event) => config.setName(event.target.value)}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="agent-prompt">
                    What should this agent do?
                  </FieldLabel>
                  <TextArea
                    id="agent-prompt"
                    value={config.prompt}
                    readOnly={!isEditor}
                    autoSize={{ minRows: 8, maxRows: 24 }}
                    onChange={(event) => config.setPrompt(event.target.value)}
                  />
                </Field>

                {config.saveError && (
                  <ErrorText>
                    Could not save the changes. Please try again.
                  </ErrorText>
                )}

                {isEditor && (
                  <ActionRow>
                    <Button
                      variant="primary"
                      disabled={!canSave || config.isSaving}
                      loading={config.isSaving}
                      onClick={() => void config.save()}
                    >
                      Save changes
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={!config.isDirty || config.isSaving}
                      onClick={config.reset}
                    >
                      Discard
                    </Button>
                  </ActionRow>
                )}
              </SectionCard>
            </>
          )}
        </ContentWrapper>
      </PageContainer>
    </PageShell>
  )
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #717680;
  letter-spacing: 0.04em;
`

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
`

const ErrorText = styled.div`
  font-size: 13px;
  color: #b42318;
`
